"use client";

import { Sparkles } from "lucide-react";

interface Props {
  /** Total USD this post has earned its author via post-author referrals. */
  earnings: number | null | undefined;
  /** Author display name (without leading @). When omitted, the badge omits the handle. */
  username?: string | null;
  /** Compact variant for inline rendering inside dense activity cards. */
  compact?: boolean;
}

/**
 * Social-proof badge that surfaces how much a post has earned its author
 * via the post-author referral system. Hides at amounts below $0.0008
 * (less than a tenth of a cent) so we never display rounding noise as if
 * it were earnings, but shows real-but-small amounts ($0.0013 etc.) — at
 * the platform's current 10% rate on cent-scale builder fees, that's
 * where most early posts will land, and seeing an actual number is way
 * more motivating than waiting until something hits $0.005.
 *
 * Two variants:
 *  - default (compact=false): full pill, used on the post detail page where
 *    we have horizontal room. "@user earned $X shilling this market".
 *  - compact (compact=true): inline subtle line for dense activity cards.
 *    Same copy, smaller type, no pill background.
 */
export default function PostEarningsBadge({ earnings, username, compact = false }: Props) {
  const amount = typeof earnings === "number" ? earnings : 0;
  if (!Number.isFinite(amount) || amount < 0.0008) return null;

  const formatted = amount >= 1
    ? `$${amount.toFixed(2)}`
    : amount >= 0.01
    ? `$${amount.toFixed(3)}`
    : `$${amount.toFixed(4)}`;

  // username can be a raw wallet (0x…) for users without a Polymarket
  // handle — truncate so the badge doesn't blow out the post card.
  const handle = username
    ? username.startsWith('0x')
      ? `${username.slice(0, 6)}…${username.slice(-4)}`
      : `@${username}`
    : "Author";

  if (compact) {
    // 2026-05-22 (Pika audit): the compact mode used to be plain
    // text and got lost between the post card and the like/comment
    // row. Pika called out that the shilling earnings line is one
    // of the product's signature features and should pop visually.
    // Now uses the same emerald pill shape as the non-compact
    // variant, just dialled down in padding / icon size so it stays
    // inline-feed-friendly. Kept emerald (the positive/payment
    // colour the rest of the product uses for PnL+) rather than
    // lime — lime is reserved for the Share-to-Earn CTA which is
    // the upstream entry point for this feature.
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] bg-emerald-500/10 border border-emerald-500/20 text-xs font-satoshi text-emerald-600 dark:text-emerald-400">
        <Sparkles className="w-3 h-3 flex-shrink-0" strokeWidth={2.4} />
        <span>
          <span className="font-semibold">{handle}</span> earned{" "}
          <span className="font-bold">{formatted}</span> shilling this market
        </span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[5px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400">
      <Sparkles className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.4} />
      <span className="text-sm font-satoshi">
        <span className="font-semibold">{handle}</span> earned{" "}
        <span className="font-bold">{formatted}</span> shilling this market
      </span>
    </div>
  );
}
